import { useAuth } from '../../hooks/useAuth.jsx'
import {
  UNDO_WINDOW_ENDS_TEXT,
  noticeSectionsFor,
} from '../constants/announcement.js'

// ─────────────────────────────────────────────────────────────────────────────
// TEMPORARY post-update announcement.
// Shows once per sign-in for any authenticated user, with an Acknowledge button
// they must press to continue. The acknowledgement records who and which sign-in
// (acknowledgementMarkFor), so it re-appears after any sign-out, whichever path
// signed them out, and for a different person on the same browser.
//
// TO REMOVE LATER: delete this file, remove the <AnnouncementModal /> mount in
// App.jsx, and (optionally) drop the removeItem lines in useAuth.jsx logout().
// Bump the version suffix on ANNOUNCEMENT_ACK_KEY (shared/constants/announcement.js)
// to force it to re-show after an edit to the message.
//
// UPDATE 2 (2.99.40): what changed since 2.98.x, by role. This is the ONLY notice anyone
// gets about the update (owner decision 13c: in-app only, no email; 15b: no note to
// customers, so office roles get the sentence to use). Production 2.98.x cannot show it
// in advance, so everything here is written for someone reading it after the update.
// Only what a person can SEE changed is listed: Capital, Capex and Invoicing are
// installed but switched off, and the lighter project lists look the same.
//
// WHEN A RELEASE LANDS AFTER THIS COPY WAS WRITTEN, COME BACK HERE. This copy was written on
// 19 Sep at 2.99.36; 2.99.37 to 2.99.53 followed, most of it out of the owner's own test session,
// and none of it reached the notice until 2.99.54. Four lines were missing. The test that a line
// is needed is not "is it a change" - almost everything here is - but "would a person be surprised
// or do the wrong thing without it":
//   * something TAKEN AWAY (timekeeper review off the foreman role, 2026_11_09 migration; its own
//     header says it "belongs in the update notice, not in a surprise on Monday morning"),
//   * a FIGURE THAT MOVED on a screen people read (per diem out of a field ticket's total, 2.99.46),
//   * a LIST THAT SHRANK (field tickets scoped to the jobs you can open, 2.99.47).
// A bug fixed with no visible change of behaviour does not belong here; the changelog has it.
// ─────────────────────────────────────────────────────────────────────────────

const overlay = {
  position: 'fixed',
  inset: 0,
  zIndex: 12000,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
}

const card = {
  background: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
  maxWidth: '560px',
  width: '100%',
  maxHeight: 'calc(100vh - 2rem)',
  display: 'flex',
  flexDirection: 'column',
  padding: '1.75rem 2rem 1.5rem',
  boxSizing: 'border-box',
}

const body = {
  overflowY: 'auto',
  minHeight: 0,
}

const title = {
  margin: '0 0 1rem',
  fontSize: '1.2rem',
  fontWeight: 700,
  color: '#111827',
}

const heading = {
  margin: '1.1rem 0 0.5rem',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#111827',
}

const para = {
  margin: '0 0 0.9rem',
  fontSize: '0.95rem',
  lineHeight: 1.55,
  color: '#374151',
}

const list = {
  margin: '0 0 0.9rem',
  paddingLeft: '1.2rem',
  fontSize: '0.95rem',
  lineHeight: 1.55,
  color: '#374151',
}

const button = {
  background: '#1f2937',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '0.6rem 1.4rem',
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
}

function Section({ id, label, children }) {
  return (
    <section data-notice-section={id}>
      {label && <h3 style={heading}>{label}</h3>}
      {children}
    </section>
  )
}

const SECTIONS = {
  external: () => (
    <Section id="external">
      <p style={para}>
        The app has been updated. If a page looks out of date, close the tab and open it again.
      </p>
    </Section>
  ),
  everyone: () => (
    <Section id="everyone" label="For everyone">
      <ul style={list}>
        <li>
          <strong>A correction to the last notice:</strong> you <strong>do</strong> get a warning two
          minutes before the total sign-in time your company allows runs out. The last notice said
          there was none. Save your work when you see it.
        </li>
        <li>
          Daily report, grid view: the Submit and Save Draft bar now sits above the hours grid instead
          of behind it.
        </li>
        <li>
          <strong>A busy moment no longer signs you out.</strong> When the app was busy, which
          happens when a whole office arrives at once or while an update is running, it used to send
          people back to the sign-in page mid-work. Now it says the server is busy, and the next
          click works.
        </li>
        <li>
          <strong>The Field Tickets tab shows the tickets for the jobs you can open.</strong> It was
          showing every ticket in the company to everyone. If your list is shorter than you
          remember, that is why; <em>Only my tickets</em> is a filter under Filters.
        </li>
        <li>If a page looks out of date, close the tab and open the app again.</li>
        <li>
          If you see an error, take a screenshot and send it to me with a short note of what you were
          doing.
        </li>
      </ul>
    </Section>
  ),
  undoWindow: () => (
    <Section id="undoWindow" label={`Until ${UNDO_WINDOW_ENDS_TEXT}`}>
      <p style={para}>
        Until then we can still switch back to the previous version if something goes wrong. To keep
        that possible:
      </p>
      <ul style={list}>
        <li>Do not press <strong>Lock &amp; Save</strong> on the Master Timesheet.</li>
        <li>Do not unlock or correct a locked week.</li>
        <li>Do not start a second release or claim on a job, and do not start a unit-price claim.</li>
        <li>Everything else is fine, including marking a release invoiced.</li>
      </ul>
    </Section>
  ),
  // "(after the date above)" only while the date IS above - here and in billing's unit-price line:
  // once the undo window closes its section is no longer shown, and the words would point at nothing.
  payroll: ({ undoWindowOpen }) => (
    <Section id="payroll" label="Timesheets and payroll">
      <ul style={list}>
        <li>
          <strong>Timekeeper review is an office job.</strong> A foreman can no longer review or
          approve a daily report: not his own crew&apos;s, and not another foreman&apos;s. A report
          now waits for the office. A company whose foremen have been doing that step will feel it
          on the first Monday.
        </li>
        <li>
          <strong>Lock &amp; Save</strong> is back on the Master Timesheet
          {undoWindowOpen ? ' (after the date above)' : ''}. It locks a site&apos;s week and keeps the
          printed sheet.
        </li>
        <li>
          A locked week can be corrected: someone allowed to approve time presses{' '}
          <strong>Unlock week</strong> and gives a reason. The sheet then says{' '}
          <em>changed after it was keyed</em>, and every earlier version is kept.
        </li>
        <li>
          <strong>Correcting an earlier day now fixes later tickets.</strong> When an approval changes
          someone&apos;s hours earlier in the week, their later tickets that week are re-split
          between straight time and overtime, at the rate already on them. A ticket the client has
          signed, one that is invoiced or in an invoice package, and one whose hours were edited by
          hand are never changed: the approval names them instead, so you can deal with them.
        </li>
      </ul>
    </Section>
  ),
  billing: ({ undoWindowOpen }) => (
    <Section id="billing" label="Billing">
      <ul style={list}>
        <li>
          <strong>Mark as Invoiced</strong> now asks for the invoice number and date.
        </li>
        <li>
          A lump-sum release that would take the job&apos;s releases past its contract (plus approved
          change orders) now tells you by how much and asks <em>Save anyway?</em> The release page keeps
          a warning while the job is over.
        </li>
        <li>
          <strong>Unit price is complete</strong>
          {undoWindowOpen ? ' (after the date above)' : ''}: a claim every month,
          retainage saved on the project, each claim showing previous, this claim and to date,
          quantities to four decimals, and a claim&apos;s lines locked once anyone signs it.
        </li>
        <li>
          A signed ticket whose signed copy failed to file now says so on the ticket screen. Tell me
          when you see it, so the copy is filed.
        </li>
        <li>
          Send Copies no longer says it failed when the copies went out. Do not send them twice.
        </li>
        <li>
          <strong>A field ticket&apos;s total is what the client owes.</strong> Per diem is no
          longer counted inside it - it sits on its own line underneath, marked absorbed and not
          billed. A ticket carrying per diem reads lower than it did by that amount, and the invoice
          now agrees with the ticket.
        </li>
        <li>
          Holidays can still only be added before their date. If you pay or bill Veterans Day
          (11 November) or Thanksgiving (26 November), enter them now.
        </li>
      </ul>
      <p style={para}>If a customer asks why a ticket&apos;s overtime changed, this is the sentence to use:</p>
      <ul style={list}>
        <li>
          <em>
            “An earlier day that week was corrected, so the hours past the weekly limit moved to the
            later days. Tickets you have already signed do not change.”
          </em>
        </li>
      </ul>
    </Section>
  ),
}

// CONTROLLED, and it opens for nobody on its own.
//
// It used to open itself on every sign-in and hold an Acknowledge button in front of whatever the
// person had signed in to do. The owner asked for the opposite while walking staging on 2026-09-16:
// "lets not have it as a popup immediately. lets create a show changes button and have this appear
// if someone presses it." So the decision to show it now belongs entirely to the caller -
// WhatChangedButton.jsx in the header - and there is no effect in here that can change that.
//
// The button says Close, not Acknowledge: nobody is being asked to agree to anything they went
// looking for themselves.
export default function AnnouncementModal({ open = false, onClose }) {
  const { user } = useAuth()

  if (!open) return null

  const sections = noticeSectionsFor(user?.role)

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="announcement-title" style={overlay}>
      <div style={card}>
        <h2 id="announcement-title" style={title}>The app has been updated. Here is what changed.</h2>
        <div style={body}>
          {sections.map((id) => {
            const Render = SECTIONS[id]
            return <Render key={id} undoWindowOpen={sections.includes('undoWindow')} />
          })}
        </div>
        <div style={{ textAlign: 'right', marginTop: '1.25rem' }}>
          <button type="button" onClick={onClose} style={button}>Close</button>
        </div>
      </div>
    </div>
  )
}
